import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Drawer, LinearProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import url from '../../../serverUrl';
import { api as axios } from '../../../api';
import './Derivatives.css';
import MarketStatsBar from './MarketStatsBar';
import LeftPanel from './LeftPanel';
import './hlTheme.css';
import OrderForm from './pro/OrderForm';
import PairPicker from './pro/PairPicker';
import ChartPanel from './pro/ChartPanel';
import AccountPanel from './pro/AccountPanel';
import StatusBar from './pro/StatusBar';
import MarketMarquee from './pro/MarketMarquee';
import { quoteFor } from './pro/format';

// Phone layout below this width (matches the old availWidth > 700 switch,
// but follows the window instead of the physical screen).
const MOBILE_QUERY = '(max-width: 760px)';
function useIsMobile() {
  const get = () =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(MOBILE_QUERY).matches
      : false;
  const [m, setM] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_QUERY);
    const on = () => setM(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on));
  }, []);
  return m;
}

function Derivatives() {
  const params = useParams();
  const navigate = useNavigate();
  const {
    PecuPrice,
    USXMPrice,
    WalletInfo,
    syntheticsList,
    hedgeTokens,
    dbtTokens,
    derivativesList,
    projectList,
    fanList,
    holdingList,
    tokenList,
    auth_token
  } = useSelector((state) => state);
  const [tokenSymbol, setTokenSymbol] = useState(params.symbol);
  // Follow the URL when another component navigates to a market
  // (account tables' Trade links, browser back/forward).
  useEffect(() => {
    if (params.symbol && params.symbol !== tokenSymbol) setTokenSymbol(params.symbol);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.symbol]);
  const [loading, setLoading] = useState(false);
  const [bid, setBid] = useState([]);
  const [ask, setAsk] = useState([]);
  const [walletAssets, setWalletAsset] = useState(null);
  const headers = {
    Authorization: `Bearer ${auth_token}`
  };

  const [token, setToken] = useState(null);




  const fetchWalletAsset = async () => {
    axios
      .get(`${url}/api/v2/wallet/balance`, { headers })
      .then((res) => {
        setWalletAsset(res.data);
        // alert(JSON.stringify(res.data));
      })
      .catch((err) => console.log(err));
  };

  const POLL_INTERVAL = 5000;   // ms between successful polls
  const REQUEST_TIMEOUT = 15000; // ms before a request is considered hung
  const MAX_RETRIES = 3;

  const isFetchingRef = useRef(false);
  const pollTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const pendingCountRef = useRef(0);
  const fetchTokenRef = useRef(null); // always points to latest fetchToken

  // MySQL DECIMAL columns (currentPrice, price1hour, ...) arrive as strings
  // ("28.75454"), and the header/bid/ask render them with .toFixed(), which
  // only exists on numbers - "v.toFixed is not a function" blanked the whole
  // page (seen on DBT tokens). Convert every purely numeric string field to a
  // number once, here, so all render paths get numbers.
  const numericFields = (row) => {
    if (!row || typeof row !== 'object') return row;
    const out = { ...row };
    for (const [k, v] of Object.entries(out)) {
      if (typeof v === 'string' && /^-?\d+(\.\d+)?(e[-+]?\d+)?$/i.test(v.trim())) out[k] = Number(v);
    }
    return out;
  };
  const setTokenNum = (row) => setToken(numericFields(row));

  // Returns a Promise so the poller knows when the call settles
  // Only ever REPLACE the token with a real row. A poll that comes back
  // empty (cache miss, transient DB hiccup) used to call setToken(undefined),
  // which blanked the header, prices and order book until a later poll
  // happened to succeed - "data shows up, then disappears".
  const keepFirst = (rows) => {
    if (Array.isArray(rows) && rows.length > 0 && rows[0]) {
      setTokenNum(rows[0]);
      return true;
    }
    return false;
  };

  // Resolves to 'unknown' when the symbol is in none of the lists, so the
  // poller can send the user to a real market instead of a blank page.
  const fetchToken = useCallback((symbol) => {
    const cfg = { timeout: REQUEST_TIMEOUT };
    const get = (path) => axios.get(`${url}${path}`, cfg).then((res) => res.data || {});

    // Market membership comes from the live /exchange/token-list first; the
    // hard-coded Redux lists are only a fallback. Those lists were frozen
    // (27 DBTs vs 36 live, 9 Venture tokens, 1 Nexus...), so every newer
    // market - e.g. /MAGx - was "unknown" and bounced to the first synth.
    const listed = (key) => (tokenList?.[key] || []).some((t) => t?.symbol === symbol);
    if (listed('dbt')) {
      return get(`/dat/get_dat?symbol=${symbol}`).then((d) => keepFirst(d.stock));
    }
    if (listed('synthetics') || syntheticsList.includes(symbol)) {
      return get(`/crypto/get_synthetics?symbol=${symbol}`).then((d) => keepFirst(d.stock));
    } else if (listed('crypto_pairs') || derivativesList.includes(symbol)) {
      return get(`/crypto/get_derivatives?symbol=${symbol}`).then((d) => keepFirst(d.stock));
    } else if (listed('holding_tokens') || holdingList.includes(symbol)) {
      // Holding tokens live in `holding_tokens`, served by
      // /wallet/get_my_tokens_holding; /hootdex/get_my_tokens_project
      // returns nothing for them. Keep it as a fallback.
      return get(`/wallet/get_my_tokens_holding?symbol=${symbol}`).then((d) =>
        keepFirst(d.tokens) ||
        get(`/hootdex/get_my_tokens_project?symbol=${symbol}`).then((d2) => keepFirst(d2.tokens))
      );
    } else if (listed('project_tokens') || projectList.includes(symbol)) {
      return get(`/wallet/get_tokens_project_by_symbol?symbol=${symbol}`).then((d) => keepFirst(d.token));
    } else if (listed('fan_tokens') || fanList.includes(symbol)) {
      return get(`/wallet/get_tokens_fan_by_symbol?symbol=${symbol}`).then((d) => keepFirst(d.token));
    } else if (listed('hedge') || hedgeTokens.includes(symbol)) {
      return get(`/hedge/${symbol}`).then((d) => keepFirst(d.stock));
    } else if (dbtTokens.includes(symbol)) {
      return get(`/dat/get_dat?symbol=${symbol}`).then((d) => keepFirst(d.stock));
    } else if (tokenList['xmg_tokens']?.some((t) => t.symbol === symbol)) {
      return get(`/crypto/get_stable_coin?symbol=${symbol}`).then((d) => keepFirst(d.stock));
    } else if (tokenList['wrap_tokens']?.some((t) => t.symbol === symbol)) {
      return get(`/wallet/get_my_tokens_wrap_new?symbol=${symbol}`).then((d) => keepFirst(d.tokens));
    } else if (tokenList['equitrack_tokens']?.some((t) => t.symbol === symbol)) {
      return get(`/crypto/get_equitrack_token?symbol=${symbol}`).then((d) => keepFirst(d.token));
    }

    // Token lists still arriving from Redux (EquiTrack, stablecoin and wrap
    // tokens are only known once /exchange/token-list lands). Report
    // 'pending' so the poller re-checks in a moment instead of waiting a
    // full POLL_INTERVAL - that wait is why EquiTrack took ~7s to appear.
    const listsLoaded =
      syntheticsList.length > 0 && Object.keys(tokenList || {}).length > 0;
    return Promise.resolve(listsLoaded ? 'unknown' : 'pending');
  }, [syntheticsList, derivativesList, holdingList, projectList, fanList,
      hedgeTokens, dbtTokens, tokenList]);

  // Keep ref current so the poller always calls the latest version
  useEffect(() => { fetchTokenRef.current = fetchToken; }, [fetchToken]);

  const runPoll = useCallback((symbol) => {
    if (isFetchingRef.current) {
      // Previous request still in flight — check again in 1s
      pollTimerRef.current = setTimeout(() => runPoll(symbol), 1000);
      return;
    }

    isFetchingRef.current = true;

    fetchTokenRef.current(symbol)
      .then((outcome) => {
        if (outcome === 'pending' && pendingCountRef.current < 30) {
          // fast re-check for up to ~9s, then fall back to the normal cadence
          pendingCountRef.current += 1;
          isFetchingRef.current = false;
          pollTimerRef.current = setTimeout(() => runPoll(symbol), 300);
          return;
        }
        if (outcome !== 'pending') pendingCountRef.current = 0;
        if (outcome === 'unknown' && syntheticsList.length > 0) {
          // e.g. /PECU - not a tradable pair. The dropdown already falls back
          // to its first option, so move the page there too instead of
          // showing an empty market.
          isFetchingRef.current = false;
          const fallback = syntheticsList[0];
          setTokenSymbol(fallback);
          navigate(`/${fallback}`, { replace: true });
          return;
        }
        retryCountRef.current = 0;
        isFetchingRef.current = false;
        setLoading(false);
        pollTimerRef.current = setTimeout(() => runPoll(symbol), POLL_INTERVAL);
      })
      .catch((err) => {
        isFetchingRef.current = false;
        const isTimeout = err.code === 'ECONNABORTED';
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current += 1;
          // Exponential backoff: 2s, 4s, 8s
          const backoff = Math.min(2000 * Math.pow(2, retryCountRef.current - 1), 30000);
          console.warn(`[poll] ${isTimeout ? 'timeout' : 'error'} — retry ${retryCountRef.current}/${MAX_RETRIES} in ${backoff}ms`);
          pollTimerRef.current = setTimeout(() => runPoll(symbol), backoff);
        } else {
          // Max retries hit — reset and resume normal cadence
          console.error('[poll] max retries reached, resuming normal cadence');
          retryCountRef.current = 0;
          setLoading(false);
          pollTimerRef.current = setTimeout(() => runPoll(symbol), POLL_INTERVAL);
        }
      });
  }, [syntheticsList, navigate]);

  useEffect(() => {
    if (!!auth_token) {
      fetchWalletAsset();
    }
  }, [auth_token]);
  // The moment the token lists land (fetchToken is rebuilt with them), stop
  // waiting for the next 300ms 'pending' re-check and fetch right away.
  useEffect(() => {
    if (pendingCountRef.current > 0 && !isFetchingRef.current) {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      runPoll(tokenSymbol);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchToken]);

  // Start/restart the poller whenever the symbol changes
  useEffect(() => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    isFetchingRef.current = false;
    retryCountRef.current = 0;
    pendingCountRef.current = 0;
    setLoading(true);
    fetchWalletAsset();
    runPoll(tokenSymbol);
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      isFetchingRef.current = false;
    };
  }, [tokenSymbol, runPoll]);

  const isMobile = useIsMobile();
  const [pickedPrice, setPickedPrice] = useState(null);
  const [sheetSide, setSheetSide] = useState(null);
  const marketSymbol = token?.identitySymbol || token?.token_symbol || token?.symbol;

  const selectMarket = (sym) => {
    setTokenSymbol(sym);
    navigate(`/${sym}`);
  };

  // Tab title like other exchanges: "81,540.12 | hBTC | HootDex".
  useEffect(() => {
    const p = Number(token?.currentPrice ?? token?.token_price);
    if (marketSymbol) {
      document.title = `${Number.isFinite(p) && p > 0 ? `${p.toLocaleString('en-US', { maximumFractionDigits: p >= 1 ? 2 : 6 })} | ` : ''}${marketSymbol} | HootDex Exchange`;
    }
  }, [token, marketSymbol]);

  const header = (
    <div className="hl-panel x-top">
      <PairPicker
        tokenList={tokenList}
        value={tokenSymbol}
        token={token}
        quote={token ? quoteFor(token, PecuPrice, USXMPrice).symbol : undefined}
        onSelect={selectMarket}
      />
      <MarketStatsBar
        token={token}
        walletConnected={Boolean(WalletInfo)}
        walletTotal={walletAssets?.total}
      />
    </div>
  );

  // LeftPanel also feeds bid/ask to the order form, so it stays mounted
  // (hidden, not removed) whichever view is showing.
  const book = (
    <LeftPanel
      tokenData={token}
      tokenSymbol={tokenSymbol}
      lastAsk={(a) => setAsk(a)}
      lastBid={(b) => setBid(b)}
      onPickPrice={(price, side) => {
        setPickedPrice({ price, side, at: Date.now() });
        if (isMobile) setSheetSide(side === 'ask' ? 'buy' : 'sell');
      }}
    />
  );

  const orderForm = (side) => (
    <OrderForm
      token={token}
      PecuPrice={PecuPrice}
      USXMPrice={USXMPrice}
      bid={bid}
      ask={ask}
      auth_token={auth_token}
      walletAssets={walletAssets}
      fetchWalletAsset={fetchWalletAsset}
      pickedPrice={pickedPrice}
      initialSide={side}
      onSubmitted={() => isMobile && setSheetSide(null)}
    />
  );

  if (isMobile) {
    return (
      <div className="hl-page x-trade x-mobile">
        {loading && <LinearProgress className="x-progress" />}
        <MarketMarquee />
        {header}
        <div className="hl-panel x-m-main">
          <ChartPanel
            token={token}
            PecuPrice={PecuPrice}
            USXMPrice={USXMPrice}
            extra={{ key: 'book', label: 'Order Book', node: <div className="hl-book">{book}</div> }}
          />
        </div>
        <div className="hl-panel hl-bottom">
          <AccountPanel walletAssets={walletAssets} auth_token={auth_token} market={marketSymbol} compact />
        </div>
        <div className="x-m-actions">
          <button type="button" className="x-of-submit x-buy" onClick={() => setSheetSide('buy')}>
            Buy
          </button>
          <button type="button" className="x-of-submit x-sell" onClick={() => setSheetSide('sell')}>
            Sell
          </button>
        </div>
        <Drawer
          anchor="bottom"
          open={Boolean(sheetSide)}
          onClose={() => setSheetSide(null)}
          PaperProps={{ className: 'x-sheet' }}
        >
          <div className="x-sheet-grip" />
          {sheetSide && orderForm(sheetSide)}
        </Drawer>
      </div>
    );
  }

  return (
    <div className="hl-page x-trade">
      {loading && <LinearProgress className="x-progress" />}
      <MarketMarquee />
      {header}
      <div className="hl-main">
        <div className="hl-panel hl-chart">
          <ChartPanel token={token} PecuPrice={PecuPrice} USXMPrice={USXMPrice} />
        </div>
        <div className="hl-panel hl-book">{book}</div>
        <div className="hl-panel hl-form">{orderForm('buy')}</div>
      </div>
      <div className="hl-panel hl-bottom">
        <AccountPanel walletAssets={walletAssets} auth_token={auth_token} market={marketSymbol} />
      </div>
      <StatusBar PecuPrice={PecuPrice} />
    </div>
  );
}
export default React.memo(Derivatives);
